<template lang="pug">
  mixin expandIcon
    .col-auto.my-auto.sidebar-link.collapse-icon
      span.when-opened
        svg.bi.bi-dash-circle(xmlns="http://www.w3.org/2000/svg", width="16", height="16", fill="currentColor", class="", viewBox="0 0 16 16")
          path(d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z")
          path(d="M4 8a.5.5 0 0 1 .5-.5h7a.5.5 0 0 1 0 1h-7A.5.5 0 0 1 4 8z")
      span.when-closed
        svg.bi.bi-plus-circle(xmlns="http://www.w3.org/2000/svg", width="16", height="16", fill="currentColor", viewBox="0 0 16 16")
          path(d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z")
          path(d="M8 4a.5.5 0 0 1 .5.5v3h3a.5.5 0 0 1 0 1h-3v3a.5.5 0 0 1-1 0v-3h-3a.5.5 0 0 1 0-1h3v-3A.5.5 0 0 1 8 4z")

  div(:class='`.${prefix}-sidebar`')
    Loading(:showMessages='false', v-if='tableOfContentsIsEmpty')

    template(v-else)
      .row.mx-0.pointy.pad-level-0(data-toggle='collapse', :href='`#${getHeaderId}`', :class='!isReportInPathClass ? "collapsed" : ""')
        .col.sidebar-nav(:class='isReportInPathClass')
          div {{ topLevelLabel }}
        +expandIcon
      .collapse(:id='getHeaderId', :class='isReportInPathClass ? "show" : ""')
        template(v-for='category in dataRows')
          .row.mx-0.pointy.pad-level-0(data-toggle='collapse', :href='"#" + getCategoryId(category)', :class='!shouldBeExpanded(category) ? "collapsed" : ""')
            .col.sidebar-link.first-level(:class='categoryClass(category)')
              .my-1 {{ category.name }}
            +expandIcon
          .collapse.group-1(:id='getCategoryId(category)', :class='shouldBeExpanded(category) ? "show" : ""')
            //- report groups
            template(v-for='group in category.groups')
              .row.pointy.mx-0.pad-level-1(data-toggle='collapse', :href='"#" + getGroupId(group)', :id='"category-" + getGroupId(group)', :class='!shouldBeExpanded(group) ? "collapsed" : ""')
                .col.sidebar-link(:class='groupClass(group)')
                  .my-1 {{ group.name }}
                +expandIcon
              .collapse.group-2(:id='getGroupId(group)', :class='shouldBeExpanded(group) ? "show" : ""')
                .row.mx-0.third-level.pad-level-2(v-for='report in group.reports', v-if='reportIsAvailable(report)')
                  .col.sidebar-link.my-1(:id='"report-link-" + report.id', :class='isSelectedClass(report)')
                    a(v-if='report.external', :href='report.link', target='_blank') {{ report.name }}
                    a(v-else-if='report.link', :href='report.link') {{ report.name }}
                    router-link(v-else, :to='getReportLink(report)', @click.native='clickLink') {{ report.name }}

            //- category level reports
            .row.mx-0.third-level.mx-0.pad-level-1(v-for='report in category.reports', v-if='reportIsAvailable(report)')
              .col.sidebar-link.my-1(:id='"report-link-" + report.id', :class='isSelectedClass(report)')
                a(v-if='report.external', :href='report.link', target='_blank') {{ report.name }}
                a(v-else-if='report.link', :href='report.link') {{ report.name }}
                router-link(v-else, :to='getReportLink(report)', @click.native='clickLink') {{ report.name }}
</template>

<script>
import Loading from '@components/Loading';
import { has, find, some } from 'lodash';
import { mapGetters } from 'vuex';

const _ = { has, find, some };

const CLASS_ACTIVE = 'active';

export default {
  name: 'ReportDirectorySidebar',
  components: {
    Loading
  },
  props: {
    prefix: String,
    onLinkClick: Function,
    dataKey: String,
    topLevelLabel: String
  },
  computed: {
    ...mapGetters('content', [
      'findReportForProperties',
      'tableOfContents',
      'tableOfContentsIsEmpty'
    ]),
    ...mapGetters('session', [
      'sessionHasRole'
    ]),
    isReportInPathClass: function () {
      return _.some(this.dataRows, this.shouldBeExpanded) ? CLASS_ACTIVE : '';
    },
    reportId: function () {
      return _.has(this.$route, 'params.reportId') ? this.$route.params.reportId : '';
    },
    reportLink: function () {
      return `#${this.$route.path}`;
    },
    getHeaderId: function () {
      return `${this.prefix}Header`;
    },
    dataRows: function () {
      return this.tableOfContents[this.dataKey];
    }
  },
  methods: {
    getNiceName(value) {
      return value ? value.replace(/[\W]*/gm, '') : '';
    },
    getCategoryId(categoryObj) {
      return this.getNiceName(categoryObj.id);
    },
    getGroupId(group) {
      return this.getNiceName(group.id);
    },
    getReportLink(report) {
      return {
        name: 'report',
        params: {
          reportId: report.id
        }
      };
    },
    shouldBeExpanded(obj) {
      // if its reports or groups/reports contains the report
      let found = _.find(obj.reports, report => report.id === this.reportId || report.link === this.reportLink);

      // not found in the reports, search the groups
      if (!found) {
        found = _.find(obj.groups, (group) => {
          return _.find(group.reports, report => report.id === this.reportId || report.link === this.reportLink);
        });
      }

      return found;
    },
    isSelectedClass(report) {
      return this.reportId === report.id || this.reportLink === report.link ? CLASS_ACTIVE : '';
    },
    groupClass: function (group) {
      return _.find(group.reports, report => report.id === this.reportId || report.link === this.reportLink) ? CLASS_ACTIVE : '';
    },
    categoryClass: function (category) {
      const hasReportMatch = this.groupClass(category);
      if (hasReportMatch) {
        return hasReportMatch;
      }

      // search the groups
      return _.find(category.groups, group => this.groupClass(group)) ? CLASS_ACTIVE : '';
    },
    reportIsAvailable(report) {
      return !report.hideNav;
    },
    clickLink() {
      if (this.onLinkClick) {
        this.onLinkClick();
      }
    }
  }
}

</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style lang="stylus" scoped>
  @import '../assets/app.styl'

  .group-1
    background-color dark-background-color-active

  .group-2
    background-color dark-background-color

  a
    color white
    &:hover
      color highlight-color

  .router-link-active
    color highlight-color

  .active
    color highlight-color
    a
      color highlight-color !important

  .sidebar-nav
    padding-top 10px
    padding-left 0px
    padding-right 0px
    padding-bottom 10px
    &:hover
      color highlight-color

  .sidebar-link
    padding-top 5px
    padding-left 0px
    padding-right 5px
    padding-bottom 5px
    &:hover
      color highlight-color

  .underline
    border-bottom 1px solid dark-mode-text-color

  .pointy
    cursor pointer

  .collapsed > .collapse-icon > .when-opened, :not(.collapsed) > .collapse-icon > .when-closed
    display none

  .link-highlight
    &:hover
      background-color dark-background-color-active

  .pad-level-0
    padding-left 15px
    padding-right 15px

  .pad-level-1
    padding-left 25px
    padding-right 15px
    border-bottom 1px dark-background-color solid

  .pad-level-2
    padding-left 35px
    padding-right 15px
    border-bottom 1px dark-background-color-active solid
</style>
